import { LANG_NAMES, type Lang, type Locale } from "../config/langs";

// A translation entry is either a plain string, or a function that
// builds one at call-site (for dynamic values: counts, language names…).
// We keep the function shape loose — callers use the `t()` helper which
// forwards variadic args.
export type DictEntry = string | ((...args: any[]) => string);
export type Dict = Record<string, DictEntry>;

export const I18N: Record<Locale, Dict> = {
  fr: {
    tagline: "the quiet way to fluency",
    theme_label: "apparence",
    aide_label: "aide",
    add_card_menu_label: "ajouter",
    search_menu_label: "rechercher",
    contact_copied: "copié ✓",
    theme_to_light: "Passer en mode clair",
    theme_to_dark: "Passer en mode sombre",
    level_pill_label: "niv",
    level_pill_title: "Voir ton niveau et la décomposition par niveau CEFR",
    lang_title: "Langue de l'interface",
    learn_label: "j'apprends",
    dir_label: "sens",
    dir_left: "lire",
    dir_right: "écrire",
    dir_preset_label: "Sens de traduction",
    dir_preset_label_mono: "Sens des questions",
    dir_preset_read: "Lecture",
    dir_preset_balanced: "Équilibré",
    dir_preset_write: "Écriture",
    dir_phrase_from: "de",
    dir_phrase_to: "vers",
    menu_label_diff: "difficulté",
    menu_label_lang: "langue",
    menu_label_filter: "filtre",
    menu_label_deck_settings: "réglages",
    deck_settings_title_prefix: "Réglages — ",
    deck_settings_sub: "Tes préférences de jeu",
    deck_settings_close: "Fermer",
    deck_settings_btn_title: "Réglages",
    deck_settings_audio_label: "Audio",
    deck_settings_tts_enabled: "Prononciation (bouton ♪)",
    deck_settings_tts_autoplay: "Lecture auto à la révélation",
    deck_settings_level_btn: "Détail par niveau CEFR",
    deck_stats_title_prefix: "Statistiques — ",
    deck_stats_sub: "Ta progression",
    deck_stats_close: "Fermer",
    card_arrow: "→",
    answer_ph: (target: Lang) => "Réponse en " + LANG_NAMES.fr[target] + "…",
    answer_ph_mono: (faceLabel: string) => "Réponse : " + faceLabel + "…",
    list_capitals: "Capitales du monde",
    face_country: "pays",
    face_capital: "capitale",
    capitales_dir_label: "Pays ou capitale ?",
    list_departements: "Départements français",
    face_departement: "département",
    face_prefecture: "chef-lieu",
    departements_dir_label: "Département ou chef-lieu ?",
    list_etats_us: "États américains",
    face_state: "état",
    etats_us_dir_label: "État ou capitale ?",
    theme_languages: "Langues",
    theme_geography: "Géographie",
    wizard_theme_label: "Que veux-tu apprendre ?",
    wizard_pick_theme: "Choisis un thème.",
    btn_validate: "Valider",
    btn_dont_know: "Je ne sais pas",
    btn_hint: "Indice",
    btn_next: "Suivante",
    search_btn_title: "Rechercher une carte",
    search_title: "Rechercher",
    search_placeholder: (a: string, b: string) =>
      `Chercher (${a} / ${b})…`,
    search_no_results: "Aucun résultat",
    search_empty_hint: "Tape quelques lettres pour chercher",
    search_close: "Fermer",
    session_end_mastery: (n: number, total: number) =>
      `maîtrisées · ${n} / ${total}`,
    session_end_mastery_tooltip:
      "Une carte est maîtrisée après 4 bonnes réponses espacées (méthode Leitner : 5 boîtes, intervalles 1/3/7/14 jours). Minimum ~11 jours de pratique régulière.",
    session_end_due_tomorrow: (n: number) =>
      `demain · ${n} carte${n > 1 ? "s" : ""} à revoir`,
    fb_correct: "correct",
    fb_expected: "attendu",
    fb_answer: "réponse",
    fb_synonym: "synonyme",
    agreement_label: "accords",
    locale_label: "Langue du site",
    font_size_label: "Taille du texte",
    flag_modal_title: "Corriger la carte",
    flag_modal_sub: "Modifie les traductions et enregistre",
    flag_modal_reason: "Note (optionnel)",
    flag_modal_save: "Enregistrer",
    flag_modal_cancel: "Annuler",
    flag_modal_saving: "Envoi…",
    flag_modal_ok: "Enregistré ✓",
    flag_modal_err: "Erreur :",
    add_card_title: "Ajouter une carte",
    add_card_sub: "Renseigne au moins deux langues",
    add_card_tags_label: "Tags (séparés par virgule)",
    add_card_tags_ph: "ex: noun, food",
    add_card_level_label: "Niveau CEFR",
    add_card_save: "Ajouter",
    add_card_cancel: "Annuler",
    add_card_btn_title: "Ajouter une nouvelle carte",
    add_card_need_two: "Renseigne au moins deux langues.",
    add_card_dup: "Une carte avec cette traduction existe déjà.",
    add_card_sending: "Envoi…",
    add_card_ok: "Carte ajoutée ✓",
    add_card_err: "Erreur :",
    add_card_no_sync: "Sync désactivée — renseigne ton prénom dans ton profil.",
    add_card_suggest: "✨ Compléter avec l'IA",
    add_card_suggest_hint: "Renseigne au moins une langue puis clique",
    add_card_suggesting: "L'IA réfléchit…",
    add_card_suggest_empty: "Tape d'abord un mot dans une des langues.",
    add_card_suggest_err: "L'IA n'a pas répondu :",
    add_card_suggest_unsupported:
      "Le webhook ne supporte pas encore l'IA. Dans Apps Script : Deploy → Manage deployments → ✏️ → Version: New version → Deploy (puis exécute authorize() une fois si UrlFetchApp n'est pas autorisé).",
    diff_easy: "facile",
    diff_adaptive: "adaptatif",
    diff_challenge: "difficile",
    diff_easy_desc: "cartes plus faciles",
    diff_adaptive_desc: "autour de ton niveau",
    diff_challenge_desc: "cartes plus dures",
    mode_title: "Mode",
    mode_libre: "libre",
    mode_defi: "défi",
    mode_revision: "révision",
    mode_libre_desc: "sans limite",
    mode_defi_desc: "50 cartes, score final",
    mode_revision_desc: "cartes à revoir",
    defi_progress: (n: number, total: number) => `défi ${n}/${total}`,
    defi_end_title: "Résultat du défi",
    defi_score: (correct: number, total: number) =>
      `${correct} / ${total} correct${correct > 1 ? "s" : ""}`,
    defi_perfect: "Sans faute",
    defi_errors_title: "À retravailler",
    defi_restart: "Nouveau défi",
    defi_back: "Mode libre",
    defi_resume_title: "Défi en cours",
    defi_resume_sub: (done: number, total: number) =>
      `Tu as répondu à ${done} / ${total} cartes. Reprendre ou recommencer ?`,
    defi_resume_continue: "Reprendre",
    defi_resume_abandon: "Nouveau défi",
    revision_empty: "Aucune carte à revoir — tout est à jour.",
    revision_done: "Révision terminée — toutes les cartes dues sont faites.",
    filter_class_title: "Filtrer par classe",
    filter_class_all: "Tous",
    filter_class_reset: "Réinitialiser",
    filter_axis_class: "Classe",
    filter_axis_domain: "Domaine",
    filter_axis_register: "Registre",
    filter_empty: "Aucune carte ne correspond au filtre.",
    filter_empty_action: "Réinitialiser le filtre",
    stats_seen: "vues",
    stats_due: "à revoir",
    stats_rate: "réussies",
    stats_session: "cette série",
    stats_session_empty: "cette série — en attente",
    stats_acquired: "acquises",
    mastery_label: "Répartition des cartes",
    mastery_new: "nouvelles",
    mastery_b1: "à réapprendre",
    mastery_b2: "récentes",
    mastery_b3: "en cours",
    mastery_b4: "presque acquises",
    mastery_b5: "acquises",
    flag_title: "Signaler une erreur sur cette carte",
    skip_title: "Passer",
    loader: "chargement…",
    load_failed: "Échec du chargement : ",
    load_timeout: "Le chargement prend trop de temps. Vérifie ta connexion et réessaie.",
    load_retry: "Réessayer",
    list_general: "Mots courants",
    list_title: "Catalogue",
    list_overflow_label: "catalogue",
    parse_error_min:
      "Colonnes manquantes : il faut au moins 2 colonnes de langue (fr-FR, bg-BG, en-GB).",
    parse_error_empty: "Le Sheet semble vide.",
    parse_error_no_cards: "Aucune carte exploitable trouvée.",
    modal_title: "Ton niveau",
    modal_subtitle: "Estimation basée sur ton taux de réussite par niveau CEFR",
    modal_eval_pending: (need: number, have: number) =>
      `réponds à au moins <b>${need}</b> cartes pour démarrer l'évaluation<br>(${have} pour l'instant)`,
    modal_provisional: (have: number, need: number) =>
      `niveau provisoire — pas encore assez de cartes vues à ce niveau<br>pour confirmer (${have}/${need})`,
    modal_confirmed: (pct: number, count: number, lv: string) =>
      `confirmé — <b>${pct}%</b> de réussite sur <b>${count}</b> cartes<br>au niveau ${lv}`,
    modal_th_level: "niveau",
    modal_th_seen: "vues",
    modal_th_rate: "réussite",
    modal_sample: "(échantillon)",
    modal_close: "Fermer",
    flag_prompt:
      "Que se passe-t-il avec cette carte ?\n(traduction fausse, faute d'orthographe, niveau aberrant, doublon…)",
    flag_no_reason: "(aucune raison)",
    sync_local: "local",
    sync_synced: "synchro",
    sync_pending: "à synchro",
    sync_syncing: "synchro…",
    sync_error: "erreur",
    sync_offline: "hors ligne",
    tts_play: "Écouter",
    sync_disabled_title: "Sync désactivée (renseigne ton prénom dans ton profil)",
    sync_user_title: (name: string) => "Utilisateur : " + name,
    sync_error_title: "La dernière sync a échoué — réessai au prochain push",
    user_btn_title: "Profil et préférences",
    user_btn_empty: "anonyme",
    user_modal_title: "Profil",
    user_modal_name_label: "Prénom",
    user_modal_learn_label: "J'apprends",
    user_modal_source_label: "Depuis",
    user_modal_stats_label: "Statistiques",
    user_modal_close: "Fermer",
    user_modal_help_btn: "Comment ça marche",
    modal_pick_pair: "Choisis une langue d'apprentissage et une langue source.",
    wizard_title: "Bienvenue",
    wizard_sub: "Quelques questions pour commencer",
    wizard_intro:
      "Karta Pamet t'aide à mémoriser par cartes. Plus tu pratiques, plus l'app apprend ton rythme et pioche des cartes adaptées.",
    wizard_name_label: "Ton prénom",
    wizard_name_ph: "prénom",
    intro_screen_desc: "Cette app t'aide à mémoriser par cartes, en s'adaptant à ton rythme. Quelques questions rapides pour commencer.",
    wizard_locale_label: "Langue de l'interface",
    wizard_target_label: "Langue à apprendre",
    wizard_source_label: "Depuis quelle langue",
    wizard_level_label: "Ton niveau actuel",
    wizard_level_none: "débutant",
    wizard_continue: "Continuer",
    wizard_pick_name: "Dis-nous ton prénom pour sauvegarder tes stats.",
    wizard_pick_target: "Choisis une langue à apprendre.",
    wizard_pick_source: "Choisis depuis quelle langue traduire.",
    intro_title: "Comment ça marche",
    intro_b1_title: "Tu vois une question, tu tapes la réponse",
    intro_b1_sub: 'Entrée pour valider, <b>◐</b> pour un indice, ou "Je ne sais pas" si tu bloques.',
    intro_b2_title: "Passer ou signaler",
    intro_b2_sub: "<b>→</b> passer, <b>⚠</b> signaler une erreur. <b>Échap</b> ferme toute fenêtre ouverte.",
    intro_b3_title: "Tout le reste dans le menu ☰",
    intro_b3_sub: "Catalogue / réglages / mode (libre, défi, révision) / ajouter / rechercher.",
    intro_b4_title: "Ton niveau CEFR s'adapte",
    intro_b4_sub: "Les cartes bien mémorisées reviennent moins souvent (espacement Leitner).",
    intro_b5_title: "Écoute la prononciation",
    intro_b5_sub: "Clique sur <b>♪</b> à côté du mot. Lecture auto à la révélation, à activer dans les <b>⚙ Réglages</b>.",
    intro_close: "C'est parti !",
    tour_next: "Suivant",
    tour_back: "Précédent",
    tour_skip: "Passer",
    tour_done: "Terminer",
    tour_intro_title: "Karta Pamet",
    tour_intro_desc: "Cette app t'aide à mémoriser par cartes, en s'adaptant à ton rythme. Voici un rapide tour des éléments de l'interface.",
    tour_s1_title: "Ta progression",
    tour_s1_desc: "L'app suit ta progression au fil des réponses. Clique pour voir tes statistiques.",
    tour_s2_title: "Catalogue",
    tour_s2_desc: "Change de thème et de catalogue (langues, géographie…).",
    tour_s3_title: "Mode d'apprentissage",
    tour_s3_desc: "Libre (par défaut, sans limite), défi (série courte notée), ou révision (cartes à revoir).",
    tour_s4_title: "Réglages",
    tour_s4_desc: "Sens, difficulté, filtre, audio… tout ce qui règle ton jeu. Aussi accessible via ⚙ sous la carte.",
    tour_s7_title: "Actions sur la carte",
    tour_s7_desc: "⚙ réglages, ⚠ signaler une erreur, → passer la carte.",
    tour_s8_title: "Ton profil",
    tour_s8_desc: "Ton prénom, la langue du site, la taille du texte, l'apparence.",
    tour_s9_title: "Aide",
    tour_s9_desc: "Relance ce tour à tout moment depuis ici.",
    tour_s10_title: "Ajouter une carte",
    tour_s10_desc: "Crée une nouvelle carte. L'IA peut compléter les traductions à partir d'un seul mot.",
    tour_s11_title: "Rechercher une carte",
    tour_s11_desc: "Trouve une carte dans le catalogue en tapant un mot. Clic sur un résultat = saute à la carte.",
    dir_arrow: "→",
    btn_end_session: "Terminer la session",
    session_end_title: "Session terminée",
    session_end_stats: (attempts: number, correct: number, rate: number) =>
      `${attempts} cartes · ${correct} correctes · ${rate}%`,
    session_end_session_delta: (n: number) =>
      `Session : +${n} carte${n > 1 ? "s" : ""} maîtrisée${n > 1 ? "s" : ""}`,
    session_end_week_delta: (n: number) => `7 jours : +${n}`,
    session_end_month_delta: (n: number) => `30 jours : +${n}`,
    session_end_level_change: (from: string, to: string) =>
      `Niveau : ${from} → ${to}`,
    session_end_tier_change: (from: string, to: string) =>
      `Niveau : ${from} → ${to}`,
    mastery_tier_gap: (n: number) =>
      `${n} carte${n > 1 ? "s" : ""} de plus pour gagner une étoile`,
    mastery_tier_max: "Niveau maximal atteint",
    session_end_resume: "Reprendre",
    session_end_new: "Nouvelle session",
    session_end_stats_link: "Voir ma progression détaillée →",
    defi_resume_btn: "Reprendre",
    defi_end_title_interrupted: "Défi interrompu",
  },
  bg: {
    tagline: "the quiet way to fluency",
    theme_label: "вид",
    aide_label: "помощ",
    add_card_menu_label: "добави",
    search_menu_label: "търси",
    contact_copied: "копирано ✓",
    theme_to_light: "Светла тема",
    theme_to_dark: "Тъмна тема",
    level_pill_label: "ниво",
    level_pill_title: "Виж нивото си и разпределението по CEFR",
    lang_title: "Език на интерфейса",
    learn_label: "уча",
    dir_label: "посока",
    dir_preset_label: "Направление на превода",
    dir_preset_label_mono: "Посока на въпросите",
    dir_preset_read: "Четене",
    dir_preset_balanced: "Баланс",
    dir_preset_write: "Писане",
    dir_left: "чети",
    dir_right: "пиши",
    dir_phrase_from: "от",
    dir_phrase_to: "към",
    menu_label_diff: "трудност",
    menu_label_lang: "език",
    menu_label_filter: "филтър",
    menu_label_deck_settings: "настройки",
    deck_settings_title_prefix: "Настройки — ",
    deck_settings_sub: "Твоите предпочитания за играта",
    deck_settings_close: "Затвори",
    deck_settings_btn_title: "Настройки",
    deck_settings_audio_label: "Звук",
    deck_settings_tts_enabled: "Произношение (бутон ♪)",
    deck_settings_tts_autoplay: "Автоматично при разкриване",
    deck_settings_level_btn: "Детайли по ниво CEFR",
    deck_stats_title_prefix: "Статистика — ",
    deck_stats_sub: "Твоят напредък",
    deck_stats_close: "Затвори",
    card_arrow: "→",
    answer_ph: (target: Lang) => "Отговор на " + LANG_NAMES.bg[target] + "…",
    answer_ph_mono: (faceLabel: string) => "Отговор: " + faceLabel + "…",
    list_capitals: "Столици на света",
    face_country: "държава",
    face_capital: "столица",
    capitales_dir_label: "Държава или столица?",
    list_departements: "Френски департаменти",
    face_departement: "департамент",
    face_prefecture: "административен център",
    departements_dir_label: "Департамент или център?",
    list_etats_us: "Американски щати",
    face_state: "щат",
    etats_us_dir_label: "Щат или столица?",
    theme_languages: "Езици",
    theme_geography: "География",
    wizard_theme_label: "Какво искаш да научиш?",
    wizard_pick_theme: "Избери тема.",
    btn_validate: "Провери",
    btn_dont_know: "Не знам",
    btn_hint: "Подсказка",
    btn_next: "Следваща",
    search_btn_title: "Търси карта",
    search_title: "Търсене",
    search_placeholder: (a: string, b: string) =>
      `Търси (${a} / ${b})…`,
    search_no_results: "Няма резултати",
    search_empty_hint: "Напиши няколко букви за търсене",
    search_close: "Затвори",
    session_end_mastery: (n: number, total: number) =>
      `усвоени · ${n} / ${total}`,
    session_end_mastery_tooltip:
      "Картата се счита за усвоена след 4 правилни отговора с нарастващи интервали (метод на Лайтнер: 5 кутии, интервали 1/3/7/14 дни). Минимум ~11 дни редовна практика.",
    session_end_due_tomorrow: (n: number) =>
      `утре · ${n} карт${n > 1 ? "и" : "а"} за преговор`,
    fb_correct: "правилно",
    fb_expected: "очаквано",
    fb_answer: "отговор",
    fb_synonym: "синоним",
    agreement_label: "форми",
    locale_label: "Език на сайта",
    font_size_label: "Размер на текста",
    flag_modal_title: "Поправи картата",
    flag_modal_sub: "Промени преводите и запази",
    flag_modal_reason: "Бележка (по избор)",
    flag_modal_save: "Запази",
    flag_modal_cancel: "Отказ",
    flag_modal_saving: "Изпращане…",
    flag_modal_ok: "Запазено ✓",
    flag_modal_err: "Грешка:",
    add_card_title: "Добави карта",
    add_card_sub: "Попълни поне два езика",
    add_card_tags_label: "Тагове (разделени със запетая)",
    add_card_tags_ph: "напр.: noun, food",
    add_card_level_label: "Ниво CEFR",
    add_card_save: "Добави",
    add_card_cancel: "Отказ",
    add_card_btn_title: "Добави нова карта",
    add_card_need_two: "Попълни поне два езика.",
    add_card_dup: "Карта с този превод вече съществува.",
    add_card_sending: "Изпращане…",
    add_card_ok: "Картата е добавена ✓",
    add_card_err: "Грешка:",
    add_card_no_sync: "Синхронизацията е изключена — въведи името си в профила.",
    add_card_suggest: "✨ Допълни с ИИ",
    add_card_suggest_hint: "Попълни поне един език и натисни",
    add_card_suggesting: "ИИ мисли…",
    add_card_suggest_empty: "Първо напиши дума на един от езиците.",
    add_card_suggest_err: "ИИ не отговори:",
    add_card_suggest_unsupported: "Webhook още не поддържа ИИ. Обнови Apps Script.",
    diff_easy: "лесно",
    diff_adaptive: "адаптивно",
    diff_challenge: "трудно",
    diff_easy_desc: "по-лесни карти",
    diff_adaptive_desc: "около нивото ти",
    diff_challenge_desc: "по-трудни карти",
    mode_title: "Режим",
    mode_libre: "свободен",
    mode_defi: "предизвикателство",
    mode_revision: "преговор",
    mode_libre_desc: "без ограничение",
    mode_defi_desc: "50 карти, финален резултат",
    mode_revision_desc: "карти за повторение",
    defi_progress: (n: number, total: number) => `предизвикателство ${n}/${total}`,
    defi_end_title: "Резултат",
    defi_score: (correct: number, total: number) =>
      `${correct} / ${total} верн${correct > 1 ? "и" : "о"}`,
    defi_perfect: "Без грешки",
    defi_errors_title: "За преговор",
    defi_restart: "Ново предизвикателство",
    defi_back: "Свободен режим",
    defi_resume_title: "Предизвикателство в ход",
    defi_resume_sub: (done: number, total: number) =>
      `Отговорил си на ${done} / ${total} карти. Да продължим или да започнем отначало?`,
    defi_resume_continue: "Продължи",
    defi_resume_abandon: "Ново предизвикателство",
    revision_empty: "Няма карти за преговор — всичко е актуално.",
    revision_done: "Преговорът завърши — всички дължими карти са направени.",
    filter_class_title: "Филтър по клас",
    filter_class_all: "Всички",
    filter_class_reset: "Нулиране",
    filter_axis_class: "Клас",
    filter_axis_domain: "Област",
    filter_axis_register: "Регистър",
    filter_empty: "Нито една карта не отговаря на филтъра.",
    filter_empty_action: "Нулирай филтъра",
    stats_seen: "видени",
    stats_due: "за повторение",
    stats_rate: "верни",
    stats_session: "тази серия",
    stats_session_empty: "тази серия — в очакване",
    stats_acquired: "усвоени",
    mastery_label: "Разпределение на картите",
    mastery_new: "нови",
    mastery_b1: "за преговор",
    mastery_b2: "скорошни",
    mastery_b3: "в процес",
    mastery_b4: "почти усвоени",
    mastery_b5: "усвоени",
    flag_title: "Сигнализирай грешка в тази карта",
    skip_title: "Пропусни",
    loader: "зарежда…",
    load_failed: "Грешка при зареждане: ",
    load_timeout: "Зареждането отнема твърде дълго. Провери връзката и опитай отново.",
    load_retry: "Опитай отново",
    list_general: "Често срещани",
    list_title: "Каталог",
    list_overflow_label: "каталог",
    parse_error_min:
      "Липсващи колони: трябват поне 2 езикови колони (fr-FR, bg-BG, en-GB).",
    parse_error_empty: "Таблицата изглежда празна.",
    parse_error_no_cards: "Не са намерени използваеми карти.",
    modal_title: "Твоето ниво",
    modal_subtitle: "Оценка въз основа на успеха ти по нива CEFR",
    modal_eval_pending: (need: number, have: number) =>
      `отговори на поне <b>${need}</b> карти, за да започне оценката<br>(${have} засега)`,
    modal_provisional: (have: number, need: number) =>
      `временно ниво — все още няма достатъчно видени карти на това ниво<br>за потвърждение (${have}/${need})`,
    modal_confirmed: (pct: number, count: number, lv: string) =>
      `потвърдено — <b>${pct}%</b> успех на <b>${count}</b> карти<br>на ниво ${lv}`,
    modal_th_level: "ниво",
    modal_th_seen: "видени",
    modal_th_rate: "успех",
    modal_sample: "(извадка)",
    modal_close: "Затвори",
    flag_prompt:
      "Какъв е проблемът с тази карта?\n(грешен превод, правописна грешка, неправилно ниво, дубликат…)",
    flag_no_reason: "(без причина)",
    sync_local: "локално",
    sync_synced: "синхр.",
    sync_pending: "чака",
    sync_syncing: "синхр…",
    sync_error: "грешка",
    sync_offline: "офлайн",
    tts_play: "Чуй",
    sync_disabled_title: "Синхронизацията е изключена (въведи името си в профила)",
    sync_user_title: (name: string) => "Потребител: " + name,
    sync_error_title: "Последната синхронизация не успя — нов опит при следващото изпращане",
    user_btn_title: "Профил и предпочитания",
    user_btn_empty: "анонимен",
    user_modal_title: "Профил",
    user_modal_name_label: "Име",
    user_modal_learn_label: "Уча",
    user_modal_source_label: "От",
    user_modal_stats_label: "Статистика",
    user_modal_close: "Затвори",
    user_modal_help_btn: "Как работи",
    modal_pick_pair: "Избери език за изучаване и изходен език.",
    wizard_title: "Добре дошъл",
    wizard_sub: "Няколко въпроса, за да започнем",
    wizard_intro:
      "Karta Pamet ти помага да запомняш чрез карти. Колкото повече практикуваш, толкова по-точно приложението намира твоя ритъм и избира подходящи карти.",
    wizard_name_label: "Твоето име",
    wizard_name_ph: "име",
    intro_screen_desc: "Приложението ти помага да запомняш чрез карти, като се адаптира към твоя ритъм. Няколко бързи въпроса за начало.",
    wizard_locale_label: "Език на интерфейса",
    wizard_target_label: "Език за учене",
    wizard_source_label: "От кой език",
    wizard_level_label: "Твоето ниво сега",
    wizard_level_none: "начинаещ",
    wizard_continue: "Продължи",
    wizard_pick_name: "Кажи ни името си, за да запазваме статистиката.",
    wizard_pick_target: "Избери език за учене.",
    wizard_pick_source: "Избери от кой език да превеждаш.",
    intro_title: "Как работи",
    intro_b1_title: "Виждаш въпрос, пишеш отговора",
    intro_b1_sub: "Enter за потвърждение, <b>◐</b> за подсказка, или „Не знам“, ако си блокиран.",
    intro_b2_title: "Пропусни или сигнализирай",
    intro_b2_sub: "<b>→</b> пропускане, <b>⚠</b> сигнал за грешка. <b>Esc</b> затваря всеки отворен прозорец.",
    intro_b3_title: "Останалото в менюто ☰",
    intro_b3_sub: "Каталог / настройки / режим (свободен, предизвикателство, преговор) / добавяне / търсене.",
    intro_b4_title: "Твоето ниво CEFR се адаптира",
    intro_b4_sub: "Добре запомнените карти се връщат по-рядко (интервален метод Leitner).",
    intro_b5_title: "Чуй произношението",
    intro_b5_sub: "Кликни върху <b>♪</b> до думата. Автоматично при разкриване се включва в <b>⚙ Настройки</b>.",
    intro_close: "Да започнем!",
    tour_next: "Напред",
    tour_back: "Назад",
    tour_skip: "Пропусни",
    tour_done: "Готово",
    tour_intro_title: "Karta Pamet",
    tour_intro_desc: "Приложението ти помага да запомняш чрез карти, като се адаптира към твоя ритъм. Ето кратка обиколка на интерфейса.",
    tour_s1_title: "Твоят напредък",
    tour_s1_desc: "Приложението следи напредъка ти при всеки отговор. Кликни, за да видиш статистиката си.",
    tour_s2_title: "Каталог",
    tour_s2_desc: "Смени тема и каталог (езици, география…).",
    tour_s3_title: "Режим на учене",
    tour_s3_desc: "Свободен (по подразбиране, без ограничение), предизвикателство (кратка поредица с резултат) или преговор (карти за повторение).",
    tour_s4_title: "Настройки",
    tour_s4_desc: "Посока, трудност, филтър, звук… всичко за настройка на играта ти. Достъпно и чрез ⚙ под картата.",
    tour_s7_title: "Действия върху картата",
    tour_s7_desc: "⚙ настройки, ⚠ сигнал за грешка, → пропусни картата.",
    tour_s8_title: "Твоят профил",
    tour_s8_desc: "Твоето име, езикът на сайта, размерът на текста, темата.",
    tour_s9_title: "Помощ",
    tour_s9_desc: "Повтори тази обиколка по всяко време от тук.",
    tour_s10_title: "Добави карта",
    tour_s10_desc: "Създай нова карта. ИИ може да попълни преводите от само една дума.",
    tour_s11_title: "Търсене на карта",
    tour_s11_desc: "Намери карта в каталога, като напишеш дума. Клик на резултат = скачаш на картата.",
    dir_arrow: "→",
    btn_end_session: "Приключи сесията",
    session_end_title: "Сесията приключи",
    session_end_stats: (attempts: number, correct: number, rate: number) =>
      `${attempts} карти · ${correct} верни · ${rate}%`,
    session_end_session_delta: (n: number) =>
      `Сесия: +${n} научена карта${n > 1 ? "" : ""}`,
    session_end_week_delta: (n: number) => `7 дни: +${n}`,
    session_end_month_delta: (n: number) => `30 дни: +${n}`,
    session_end_level_change: (from: string, to: string) =>
      `Ниво: ${from} → ${to}`,
    session_end_tier_change: (from: string, to: string) =>
      `Ниво: ${from} → ${to}`,
    mastery_tier_gap: (n: number) =>
      `Още ${n} карт${n > 1 ? "и" : "а"} за нова звезда`,
    mastery_tier_max: "Максимално ниво",
    session_end_resume: "Продължи",
    session_end_new: "Нова сесия",
    session_end_stats_link: "Виж подробния напредък →",
    defi_resume_btn: "Продължи",
    defi_end_title_interrupted: "Предизвикателство прекратено",
  },
  en: {
    tagline: "the quiet way to fluency",
    theme_label: "appearance",
    aide_label: "help",
    add_card_menu_label: "add",
    search_menu_label: "search",
    contact_copied: "copied ✓",
    theme_to_light: "Switch to light mode",
    theme_to_dark: "Switch to dark mode",
    level_pill_label: "lvl",
    level_pill_title: "View your level and the per-CEFR breakdown",
    lang_title: "Interface language",
    learn_label: "learning",
    dir_label: "direction",
    dir_preset_label: "Translation direction",
    dir_preset_label_mono: "Question direction",
    dir_preset_read: "Reading",
    dir_preset_balanced: "Balanced",
    dir_preset_write: "Writing",
    dir_left: "read",
    dir_right: "write",
    dir_phrase_from: "from",
    dir_phrase_to: "to",
    menu_label_diff: "difficulty",
    menu_label_lang: "language",
    menu_label_filter: "filter",
    menu_label_deck_settings: "settings",
    deck_settings_title_prefix: "Settings — ",
    deck_settings_sub: "Your play preferences",
    deck_settings_close: "Close",
    deck_settings_btn_title: "Settings",
    deck_settings_audio_label: "Audio",
    deck_settings_tts_enabled: "Pronunciation (♪ button)",
    deck_settings_tts_autoplay: "Auto-play on reveal",
    deck_settings_level_btn: "CEFR level breakdown",
    deck_stats_title_prefix: "Stats — ",
    deck_stats_sub: "Your progress",
    deck_stats_close: "Close",
    card_arrow: "→",
    answer_ph: (target: Lang) => "Answer in " + LANG_NAMES.en[target] + "…",
    answer_ph_mono: (faceLabel: string) => "Answer: " + faceLabel + "…",
    list_capitals: "Capitals of the world",
    face_country: "country",
    face_capital: "capital",
    capitales_dir_label: "Country or capital?",
    list_departements: "French départements",
    face_departement: "département",
    face_prefecture: "prefecture",
    departements_dir_label: "Département or prefecture?",
    list_etats_us: "US states",
    face_state: "state",
    etats_us_dir_label: "State or capital?",
    theme_languages: "Languages",
    theme_geography: "Geography",
    wizard_theme_label: "What do you want to learn?",
    wizard_pick_theme: "Pick a theme.",
    btn_validate: "Check",
    btn_dont_know: "I don't know",
    btn_hint: "Hint",
    btn_next: "Next",
    search_btn_title: "Search a card",
    search_title: "Search",
    search_placeholder: (a: string, b: string) =>
      `Search (${a} / ${b})…`,
    search_no_results: "No results",
    search_empty_hint: "Type a few letters to search",
    search_close: "Close",
    session_end_mastery: (n: number, total: number) =>
      `mastered · ${n} / ${total}`,
    session_end_mastery_tooltip:
      "A card is mastered after 4 correct answers at increasing intervals (Leitner method: 5 boxes, intervals 1/3/7/14 days). Minimum ~11 days of regular practice.",
    session_end_due_tomorrow: (n: number) =>
      `tomorrow · ${n} card${n > 1 ? "s" : ""} to review`,
    fb_correct: "correct",
    fb_expected: "expected",
    fb_answer: "answer",
    fb_synonym: "synonym",
    agreement_label: "forms",
    locale_label: "Site language",
    font_size_label: "Text size",
    flag_modal_title: "Fix the card",
    flag_modal_sub: "Edit the translations and save",
    flag_modal_reason: "Note (optional)",
    flag_modal_save: "Save",
    flag_modal_cancel: "Cancel",
    flag_modal_saving: "Sending…",
    flag_modal_ok: "Saved ✓",
    flag_modal_err: "Error:",
    add_card_title: "Add a card",
    add_card_sub: "Fill in at least two languages",
    add_card_tags_label: "Tags (comma-separated)",
    add_card_tags_ph: "e.g. noun, food",
    add_card_level_label: "CEFR level",
    add_card_save: "Add",
    add_card_cancel: "Cancel",
    add_card_btn_title: "Add a new card",
    add_card_need_two: "Fill in at least two languages.",
    add_card_dup: "A card with this translation already exists.",
    add_card_sending: "Sending…",
    add_card_ok: "Card added ✓",
    add_card_err: "Error:",
    add_card_no_sync: "Sync disabled — set your name in the profile.",
    add_card_suggest: "✨ Fill with AI",
    add_card_suggest_hint: "Type at least one language then click",
    add_card_suggesting: "AI is thinking…",
    add_card_suggest_empty: "Type a word in one of the languages first.",
    add_card_suggest_err: "AI did not respond:",
    add_card_suggest_unsupported:
      "Webhook doesn't support AI yet. Update the Apps Script.",
    diff_easy: "easy",
    diff_adaptive: "adaptive",
    diff_challenge: "hard",
    diff_easy_desc: "easier cards",
    diff_adaptive_desc: "around your level",
    diff_challenge_desc: "harder cards",
    mode_title: "Mode",
    mode_libre: "free",
    mode_defi: "challenge",
    mode_revision: "review",
    mode_libre_desc: "no limit",
    mode_defi_desc: "50 cards, final score",
    mode_revision_desc: "due cards only",
    defi_progress: (n: number, total: number) => `challenge ${n}/${total}`,
    defi_end_title: "Challenge result",
    defi_score: (correct: number, total: number) => `${correct} / ${total} correct`,
    defi_perfect: "No errors",
    defi_errors_title: "To review",
    defi_restart: "New challenge",
    defi_back: "Free mode",
    defi_resume_title: "Challenge in progress",
    defi_resume_sub: (done: number, total: number) =>
      `You answered ${done} / ${total} cards. Resume or start over?`,
    defi_resume_continue: "Resume",
    defi_resume_abandon: "New challenge",
    revision_empty: "No cards to review — all caught up.",
    revision_done: "Review complete — all due cards are done.",
    filter_class_title: "Filter by class",
    filter_class_all: "All",
    filter_class_reset: "Reset",
    filter_axis_class: "Class",
    filter_axis_domain: "Domain",
    filter_axis_register: "Register",
    filter_empty: "No card matches the filter.",
    filter_empty_action: "Reset filter",
    stats_seen: "seen",
    stats_due: "due",
    stats_rate: "correct",
    stats_session: "this session",
    stats_session_empty: "this streak — idle",
    stats_acquired: "mastered",
    mastery_label: "Card distribution",
    mastery_new: "new",
    mastery_b1: "relearning",
    mastery_b2: "recent",
    mastery_b3: "in progress",
    mastery_b4: "almost mastered",
    mastery_b5: "mastered",
    flag_title: "Report an error on this card",
    skip_title: "Skip",
    loader: "loading…",
    load_failed: "Load failed: ",
    load_timeout: "Loading is taking too long. Check your connection and try again.",
    load_retry: "Retry",
    list_general: "Common words",
    list_title: "Catalog",
    list_overflow_label: "catalog",
    parse_error_min:
      "Missing columns: need at least 2 language columns (fr-FR, bg-BG, en-GB).",
    parse_error_empty: "The sheet appears to be empty.",
    parse_error_no_cards: "No usable cards found.",
    modal_title: "Your level",
    modal_subtitle: "Estimate based on your success rate per CEFR level",
    modal_eval_pending: (need: number, have: number) =>
      `answer at least <b>${need}</b> cards to start the evaluation<br>(${have} so far)`,
    modal_provisional: (have: number, need: number) =>
      `provisional level — not yet enough cards seen at this level<br>to confirm (${have}/${need})`,
    modal_confirmed: (pct: number, count: number, lv: string) =>
      `confirmed — <b>${pct}%</b> success on <b>${count}</b> cards<br>at level ${lv}`,
    modal_th_level: "level",
    modal_th_seen: "seen",
    modal_th_rate: "success",
    modal_sample: "(sample)",
    modal_close: "Close",
    flag_prompt:
      "What's wrong with this card?\n(wrong translation, typo, wrong level, duplicate…)",
    flag_no_reason: "(no reason)",
    sync_local: "local",
    sync_synced: "synced",
    sync_pending: "pending",
    sync_syncing: "syncing…",
    sync_error: "sync error",
    sync_offline: "offline",
    tts_play: "Listen",
    sync_disabled_title: "Sync disabled (set your name in the profile)",
    sync_user_title: (name: string) => "User: " + name,
    sync_error_title: "Last sync failed — will retry on next push",
    user_btn_title: "Profile and preferences",
    user_btn_empty: "anonymous",
    user_modal_title: "Profile",
    user_modal_name_label: "Name",
    user_modal_learn_label: "Learning",
    user_modal_source_label: "From",
    user_modal_stats_label: "Statistics",
    user_modal_close: "Close",
    user_modal_help_btn: "How it works",
    modal_pick_pair: "Pick a language to learn and a source language.",
    wizard_title: "Welcome",
    wizard_sub: "A few questions to get started",
    wizard_intro:
      "Karta Pamet helps you memorise with cards. The more you practice, the better the app learns your pace and picks suitable cards.",
    wizard_name_label: "Your name",
    wizard_name_ph: "name",
    intro_screen_desc: "This app helps you memorize with cards, adapting to your pace. Just a few quick questions to get started.",
    wizard_locale_label: "Interface language",
    wizard_target_label: "Language to learn",
    wizard_source_label: "Translate from",
    wizard_level_label: "Your current level",
    wizard_level_none: "beginner",
    wizard_continue: "Continue",
    wizard_pick_name: "Tell us your name so we can save your stats.",
    wizard_pick_target: "Pick a language to learn.",
    wizard_pick_source: "Pick which language to translate from.",
    intro_title: "How it works",
    intro_b1_title: "See a question, type the answer",
    intro_b1_sub: 'Enter to validate, <b>◐</b> for a hint, or "I don\'t know" if you\'re stuck.',
    intro_b2_title: "Skip or flag",
    intro_b2_sub: "<b>→</b> skip, <b>⚠</b> flag an error. <b>Esc</b> closes any open window.",
    intro_b3_title: "Everything else is in the ☰ menu",
    intro_b3_sub: "Catalog / settings / mode (free, challenge, review) / add / search.",
    intro_b4_title: "Your CEFR level adapts",
    intro_b4_sub: "Well-memorised cards come back less often (Leitner spacing).",
    intro_b5_title: "Hear the pronunciation",
    intro_b5_sub: "Click <b>♪</b> next to the word. Auto-play on reveal is togglable in <b>⚙ Settings</b>.",
    intro_close: "Let's go!",
    tour_next: "Next",
    tour_back: "Back",
    tour_skip: "Skip",
    tour_done: "Done",
    tour_intro_title: "Karta Pamet",
    tour_intro_desc: "This app helps you memorize with cards, adapting to your pace. Here's a quick tour of the interface.",
    tour_s1_title: "Your progress",
    tour_s1_desc: "The app tracks your progress with every answer. Tap to see your stats.",
    tour_s2_title: "Catalog",
    tour_s2_desc: "Switch theme and catalog (languages, geography…).",
    tour_s3_title: "Learning mode",
    tour_s3_desc: "Free (default, no limit), challenge (short scored run), or review (cards due to review).",
    tour_s4_title: "Settings",
    tour_s4_desc: "Direction, difficulty, filter, audio… everything that tunes your play. Also reachable via ⚙ under the card.",
    tour_s7_title: "Card actions",
    tour_s7_desc: "⚙ settings, ⚠ flag an error, → skip the card.",
    tour_s8_title: "Your profile",
    tour_s8_desc: "Your name, site language, text size, theme.",
    tour_s9_title: "Help",
    tour_s9_desc: "Replay this tour anytime from here.",
    tour_s10_title: "Add a card",
    tour_s10_desc: "Create a new card. The AI can fill in the translations from a single word.",
    tour_s11_title: "Search a card",
    tour_s11_desc: "Find a card in the catalog by typing a word. Click a result to jump straight to that card.",
    dir_arrow: "→",
    btn_end_session: "End session",
    session_end_title: "Session ended",
    session_end_stats: (attempts: number, correct: number, rate: number) =>
      `${attempts} cards · ${correct} correct · ${rate}%`,
    session_end_session_delta: (n: number) =>
      `Session: +${n} mastered card${n > 1 ? "s" : ""}`,
    session_end_week_delta: (n: number) => `7 days: +${n}`,
    session_end_month_delta: (n: number) => `30 days: +${n}`,
    session_end_level_change: (from: string, to: string) =>
      `Level: ${from} → ${to}`,
    session_end_tier_change: (from: string, to: string) =>
      `Level: ${from} → ${to}`,
    mastery_tier_gap: (n: number) =>
      `${n} more card${n > 1 ? "s" : ""} to earn a star`,
    mastery_tier_max: "Top tier reached",
    session_end_resume: "Resume",
    session_end_new: "New session",
    session_end_stats_link: "See detailed progress →",
    defi_resume_btn: "Resume",
    defi_end_title_interrupted: "Challenge interrupted",
  },
};
